'use client'

import type { Product } from '@/payload-types'

import { AddToCart } from '@/components/Cart/AddToCart'
import React, { Suspense } from 'react'

/**
 * Mobile-only bottom bar (price + Add to cart). Appears once the main
 * Add to cart button has scrolled out of view above the viewport.
 */
export function StickyBuyBar({
  product,
  price,
  targetRef,
}: {
  product: Product
  price: React.ReactNode
  targetRef: React.RefObject<HTMLElement | null>
}) {
  const [visible, setVisible] = React.useState(false)
  // Mount the bar only after it is needed for the first time, so the page
  // does not carry a second (hidden) price + Add to cart in the DOM.
  const [armed, setArmed] = React.useState(false)
  React.useEffect(() => {
    if (visible) setArmed(true)
  }, [visible])

  React.useEffect(() => {
    const el = targetRef.current
    if (!el || typeof IntersectionObserver === 'undefined') return
    const observer = new IntersectionObserver(([entry]) => {
      setVisible(!entry.isIntersecting && entry.boundingClientRect.top < 0)
    })
    observer.observe(el)
    return () => observer.disconnect()
  }, [targetRef])

  if (!armed) return null

  return (
    <div
      aria-hidden={!visible}
      data-testid="sticky-buy-bar"
      inert={!visible}
      className={`fixed inset-x-0 bottom-0 z-30 md:hidden border-t border-white/15 bg-neutral-900/90 backdrop-blur-md px-4 pt-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] transition-transform duration-200 ${
        visible ? 'translate-y-0' : 'translate-y-full pointer-events-none'
      }`}
    >
      <div className="flex items-center gap-4">
        <div className="shrink-0 text-lg font-bold text-white">{price}</div>
        <div className="min-w-0 flex-1">
          <Suspense fallback={null}>
            <AddToCart product={product} />
          </Suspense>
        </div>
      </div>
    </div>
  )
}
